import { useState, useEffect, useMemo, useRef } from 'react';
import { Search, SearchX, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  REPORT_CATEGORIES,
  REPORT_TEMPLATES,
  TEMPLATE_COLOR_CLASSES,
  type ReportCategory,
  type ReportTemplate,
} from './reportTemplates';

interface ReportBuilderProps {
  onGenerate: (prompt: string) => void;
  isGenerating: boolean;
  /** When set (e.g. from rerun), prefill the textarea and clear after applying */
  initialPrompt?: string | null;
  onInitialPromptConsumed?: () => void;
}

function matchesSearch(template: ReportTemplate, query: string): boolean {
  const q = query.toLowerCase();
  return (
    template.label.toLowerCase().includes(q) ||
    template.description.toLowerCase().includes(q) ||
    template.keywords.some((keyword) => keyword.toLowerCase().includes(q))
  );
}

function TemplateCard({
  template,
  disabled,
  onSelect,
}: {
  template: ReportTemplate;
  disabled: boolean;
  onSelect: (prompt: string) => void;
}) {
  const Icon = template.icon;
  const colors = TEMPLATE_COLOR_CLASSES[template.color];
  return (
    <button
      type="button"
      onClick={() => onSelect(template.prompt)}
      disabled={disabled}
      className={cn(
        'flex items-start gap-3 rounded-xl border bg-card p-4 text-left shadow-sm transition-colors disabled:opacity-50',
        colors.borderColor
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
          colors.iconBg,
          colors.iconColor
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground">{template.label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{template.description}</p>
      </div>
    </button>
  );
}

export function ReportBuilder({
  onGenerate,
  isGenerating,
  initialPrompt,
  onInitialPromptConsumed,
}: ReportBuilderProps) {
  const [prompt, setPrompt] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ReportCategory | 'All'>('All');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (initialPrompt != null && initialPrompt.trim() !== '') {
      setPrompt(initialPrompt.trim());
      onInitialPromptConsumed?.();
    }
  }, [initialPrompt, onInitialPromptConsumed]);

  const focusPromptTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSelectTemplate = (templatePrompt: string) => {
    setPrompt(templatePrompt);
    // Focus after state update so the caret is in the filled prompt
    requestAnimationFrame(focusPromptTextarea);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = prompt.trim();
    if (trimmed) {
      onGenerate(trimmed);
    }
  };

  const trimmedSearch = search.trim();

  const filteredTemplates = useMemo(() => {
    let templates = REPORT_TEMPLATES;
    if (category !== 'All') {
      templates = templates.filter((t) => t.category === category);
    }
    if (trimmedSearch) {
      templates = templates.filter((t) => matchesSearch(t, trimmedSearch));
    }
    return templates;
  }, [category, trimmedSearch]);

  // Group under category headings only in the default "browse everything" view
  const showGrouped = category === 'All' && !trimmedSearch;

  const groupedTemplates = useMemo(() => {
    if (!showGrouped) return [];
    return REPORT_CATEGORIES.map((cat) => ({
      category: cat,
      templates: filteredTemplates.filter((t) => t.category === cat),
    })).filter((group) => group.templates.length > 0);
  }, [showGrouped, filteredTemplates]);

  return (
    <div className="flex w-full flex-col p-4 pb-8 sm:p-6">
      <form onSubmit={handleSubmit} className="w-full">
        <p className="mb-2 text-sm font-medium text-muted-foreground">
          Describe in detail what you want to generate in the report
        </p>
        <Textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Select a template from below or type your own instructions..."
          disabled={isGenerating}
          rows={5}
          className="min-h-[100px] resize-y text-sm"
        />
        <Button
          type="submit"
          className="mt-3 w-full sm:w-auto"
          disabled={!prompt.trim() || isGenerating}
        >
          {isGenerating ? (
            <>Generating...</>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate report
            </>
          )}
        </Button>
      </form>

      <div className="mt-8 w-full">
        <p className="mb-3 text-sm font-medium text-muted-foreground">
          Or choose a template from here to get started
        </p>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="pl-9"
          />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {(['All', ...REPORT_CATEGORIES] as const).map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                category === cat
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              {cat}
            </button>
          ))}
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="mt-8 flex flex-col items-center gap-2 rounded-xl border border-dashed p-8 text-center">
            <SearchX className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">No templates found</p>
            <p className="text-xs text-muted-foreground">
              Try a different search term or category, or describe your report above.
            </p>
          </div>
        ) : showGrouped ? (
          <div className="mt-5 flex flex-col gap-6">
            {groupedTemplates.map((group) => (
              <div key={group.category}>
                <h3 className="mb-3 text-sm font-semibold text-foreground">{group.category}</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {group.templates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      disabled={isGenerating}
                      onSelect={handleSelectTemplate}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                disabled={isGenerating}
                onSelect={handleSelectTemplate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
